import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from "@angular/core";
import { DsDropdownComponent } from "@app2/shared/components/ds-dropdown.component";
import { LocalizationKey } from "@app2/shared/localization/default-strings";
import { localization } from "@app2/shared/localization/localization";
import { cloneDeep } from "lodash-es";

export interface MultiSelectOption<V extends string = string> {
    key: string;
    value: V;
}

@Component({
    selector: "savable-multiselect-dropdown",
    template: `
        <div class="default-cursor"
             *ngIf="disabled"
             [matTooltip]="tooltipText"
             [matTooltipDisabled]="!tooltipText"
             matTooltipPosition="right"
             matTooltipClass="pre-line">
            <span class="dropdown-label">{{ labelKey | localize }}:</span>
            <span class="dropdown-value">{{ selectionCount() }}</span>
        </div>
        <ds-dropdown *ngIf="!disabled"
                     [loading]="loading"
                     [maxHeight]="'200px'"
                     [absoluteWidth]="'320px'"
                     [showHeader]="false"
                     [matTooltip]="tooltipText"
                     [matTooltipDisabled]="!tooltipText"
                     matTooltipPosition="right"
                     matTooltipClass="pre-line">
            <div id="ds-dropdown-trigger">
                <span class="dropdown-label">{{ labelKey | localize }}:</span>
                <span class="dropdown-value">{{ selectionCount() }}</span>
            </div>
            <li *ngFor="let option of options"
                (click)="toggleOption(option)">
                <input type="checkbox"
                       class="dropdown-checkbox"
                       [checked]="internalSelection?.[option.value]">
                {{ option.key }}
            </li>
            <div id="ds-dropdown-footer-row" class="dropdown-footer">
                <div *ngIf="anyOptionSelected" class="reset-link">
                    <a class="de-emphasize"
                       test-class="reset-link"
                       (click)="clearFilters()">
                        <i class="fa fa-fw fa-times"></i>
                        {{ "RESET_ALL" | localize }}
                    </a>
                </div>
                <action-buttons [inline]="true"
                                [saveText]="'APPLY' | localize"
                                sizes="small"
                                (cancel)="undoFilterChanges()"
                                (save)="saveFilters()">
                </action-buttons>
            </div>
        </ds-dropdown>
    `,
    styles: [`
      .dropdown-checkbox {
        pointer-events: none
      }

      .reset-link {
        display: flex;
        align-items: center;
        flex-grow: 1;
      }

      #ds-dropdown-footer-row {
        justify-content: flex-end;
      }

      input[type=checkbox] {
        margin-left: 0;
      }

      .dropdown-footer {
        display: flex;
        justify-content: space-between;
      }

      .de-emphasize {
        white-space: nowrap;
      }
    `],
})
export class SavableMultiselectDropdownComponent<V extends string> implements OnChanges {
    @ViewChild(DsDropdownComponent) dsDropdown: DsDropdownComponent;

    @Input() disabled = false;
    @Input() loading = false;
    @Input() labelKey: LocalizationKey;
    @Input() options: MultiSelectOption<V>[];
    @Input() selection: Partial<Record<V, boolean>> = {};
    @Input() initialValue: string[];
    @Output() selectionChange = new EventEmitter<Partial<Record<V, boolean>>>();

    internalSelection: Partial<Record<V, boolean>> = {};
    tooltipText = "";

    ngOnChanges(changes: SimpleChanges) {
        if (this.initialValue !== undefined) {
            this.selection = {};
            for (const op of this.initialValue) {
                this.selection[op] = true;
            }
            this.internalSelection = cloneDeep(this.selection);
        }

        if (changes.selection) {
            this.internalSelection = cloneDeep(this.selection);
        }

        if (changes.options || changes.selection) {
            this.updateTooltipText();
        }
    }

    get anyOptionSelected(): boolean {
        return Object.values(this.internalSelection).filter(value => !!value).length > 0;
    }

    updateTooltipText() {
        if (this.options === null) {
            return;
        }
        const selectedIds = new Set(Object.entries(this.selection)
            .filter(([, selected]) => selected)
            .map(([id]) => id));

        // this is so we can preserve the same order as options
        // but what if options doesn't include something selected?
        this.tooltipText = this.options
            .filter(option => selectedIds.has(option.value))
            .map(option => `• ${ option.key }`)
            .join("\n");
    }

    toggleOption(option: MultiSelectOption<V>) {
        const uuid = option.value;
        this.internalSelection[uuid] = !this.internalSelection?.[uuid];
    }

    undoFilterChanges() {
        this.internalSelection = cloneDeep(this.selection);
        this.dsDropdown.hideDropdown();
    }

    clearFilters() {
        this.internalSelection = {};
        // do we want to close here too or just clear
    }

    saveFilters() {
        this.selection = cloneDeep(this.internalSelection);
        this.updateTooltipText();
        this.selectionChange.emit(this.internalSelection);
        this.dsDropdown.hideDropdown();
    }

    selectionCount(): string {
        if (this.options === null) {
            return "";
        }
        // For all multi-selects in here, no options selected is treated as all option selected
        const numSelected = Object.values(this.selection).reduce((a: number, b) => a + (b ? 1 : 0), 0);
        if (numSelected === 0 || numSelected === this.options.length) {
            return localization.getString("ALL");
        } else if (numSelected === 1) {
            const onlySelectedId = Object.entries(this.selection).find(entry => entry[1])![0];
            // technically it might be possible for the id to not exist if the subscription updates the options
            // to remove it while it's still "selected" in the object...
            return this.options.find(option => option.value === onlySelectedId)?.key ?? "";
        }
        return `${ localization.getString("SELECTED") } (${ numSelected })`;
    }
}
